import { UploadOutlined } from "@ant-design/icons";
import { Image } from "antd";
import { FC, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { TaskAddPhoto } from "tm-shared/types";

import BaseButton from "@/components/BaseButton";
import { GalleryUploader } from "@/components/GalleryUploader";
import Header from "@/components/Header";
import { MainContainer } from "@/components/Layout/Styled";
import { Loading } from "@/components/Loading";
import { MarketCard } from "@/components/MarketCard";
import { SVScanButton } from "@/components/SVScanButton";
import { FALLBACK_IMAGE, ROUTES } from "@/constants";
import { useAppDispatch, useAppSelector } from "@/hooks/redux";
import useAsyncAction from "@/hooks/useAsyncAction";
import { makeCommand, useMakeCommand } from "@/hooks/useRequestQueue";
import { enqueueRequestQueueItem } from "@/store/slices/offline";
import {
    clearTaskPayload,
    deleteAddPhotoTask,
    setAddPhotoTask,
} from "@/store/slices/progress";
import { setTaskCompleted } from "@/store/slices/work";
import { getFileUrl } from "@/utils/file/getFileUrl";

import { StyledAPImageWrapper, StyledGalleryWrapper } from "./Styled";

interface AddPhotoPageProps {
    marketId: string;
    task: TaskAddPhoto;
}

export const AddPhotoPage: FC<AddPhotoPageProps> = ({ marketId, task }) => {
    const dispatch = useAppDispatch();
    const { markets } = useAppSelector((state) => state.work);
    const { addPhoto } = useAppSelector((state) => state.progress);

    const navigate = useNavigate();

    const market = markets?.[marketId];
    const taskId = task.id;
    const currentPhotoId = addPhoto[taskId];

    const savedPhotos = currentPhotoId ? [currentPhotoId] : [];

    const { makeTaskCompleteCommands } = useMakeCommand();

    const [handleComplete, completeLoading] = useAsyncAction(async () => {
        const taskCompleteCommands = await makeTaskCompleteCommands({
            params: {
                taskId,
                hasPhoto: false,
                payload: { result: true },
            },
            marketId,
        });

        dispatch(
            enqueueRequestQueueItem({
                id: taskId,
                metadata: { marketId, taskId },
                commands: [
                    makeCommand({
                        type: "putMarketChangePhoto",
                        title: "Збереження фото",
                        params: { photoId: currentPhotoId, marketId },
                    }),

                    ...taskCompleteCommands,

                    makeCommand({
                        type: "deletePhotoFromStorage",
                        title: "Видалення фото зі сховища",
                        params: currentPhotoId,
                        options: {
                            stopOnError: false,
                        },
                    }),
                ],
            }),
        );

        dispatch(clearTaskPayload({ taskId }));
        dispatch(setTaskCompleted({ marketId, taskId }));
        navigate(ROUTES.WorkPlan, { replace: true });
    });

    const canSubmit = !!currentPhotoId;

    const handleDeletePhoto = useCallback(() => {
        dispatch(deleteAddPhotoTask({ taskId }));
    }, [taskId, dispatch]);

    const handleUploadPhoto = useCallback(
        (newPhotoId: string) => {
            dispatch(
                setAddPhotoTask({
                    taskId,
                    photoId: newPhotoId,
                }),
            );
        },
        [taskId, dispatch],
    );

    return (
        <>
            <Header
                title={task.name}
                back
                rightActions={
                    <SVScanButton marketId={market.id} taskId={task.id} />
                }
            />

            <MainContainer>
                <MarketCard
                    fop={market?.contragent?.name}
                    address={market?.address?.name}
                />

                <StyledAPImageWrapper>
                    <Image
                        width="100%"
                        height="100%"
                        src={getFileUrl(market?.photo)}
                        fallback={FALLBACK_IMAGE}
                    />
                </StyledAPImageWrapper>

                <StyledGalleryWrapper>
                    <GalleryUploader
                        marketId={marketId}
                        onDelete={handleDeletePhoto}
                        onUpload={handleUploadPhoto}
                        previewTitle={task.name}
                        savedPhotos={savedPhotos}
                        disabled={!!currentPhotoId}
                        galleryConfig={{ skipUpload: true }}
                    />
                </StyledGalleryWrapper>

                <BaseButton
                    type="primary"
                    icon={<UploadOutlined />}
                    onClick={handleComplete}
                    marginTop="16px"
                    disabled={!canSubmit}
                    loading={completeLoading}
                >
                    Завантажити фото
                </BaseButton>
            </MainContainer>

            {completeLoading && <Loading title="Завершення задачі" />}
        </>
    );
};
